import { fromByteArray } from 'base64-js';
import { ERROR_TYPES, DSP_INVOCATION_TYPES } from '@shared/Resources/Monitor/types';
import { LOCAL_STORAGE_ITEM, getFromLocalStorage } from '@utils/localStorageHelper';
import {
  addSampleData,
  setDSPCallback,
  getOriginalGraphicValue,
  setAnalysisType,
  calibrate,
  calibrateCancel,
} from '../NTDSPService';

const BUFFER_SIZE = 1024;
const QUEUE_SIZE = 40;
export const VOICE_LOUDNESS_AUDIO_CONSTRAINTS = {
  autoGainControl: false,
};
let CALIBRATE_AUDIO_CONTEXT;
let CALIBRATE_MICROPHONE_STREAM;
let MICROPHONE_STREAM;
let packetIndex = 0;
let AUDIO_CONTEXT;
let AUDIO_STREAM;
let SCRIPT_NODE;
let handleSampleData = {}; // this will be set on start Microphone
let audioBuffer = [];
let tmpBuffer = [];
let sending = false;
let activityId = null;
let microphoneRequestId = 0;
const sampleSubscribers = new Set();
let calibrationMeanSquares = [];
let calibratedNoiseFloorMeanSquare = null;
let stopCalibrationOnDSPCallback = true;

export const subscribeToMicrophoneSamples = (subscriber) => {
  sampleSubscribers.add(subscriber);
  return () => sampleSubscribers.delete(subscriber);
};

export const getCalibratedNoiseFloorMeanSquare = () => calibratedNoiseFloorMeanSquare;

export const setActivityId = async (id) => {
  activityId = id;
  packetIndex = 0;
};

const convertFloatArrayToBase64 = (data) => {
  const int8Data = new Uint8Array(data.buffer);
  const stringArray = fromByteArray(int8Data);
  return stringArray;
};

const sendAudioBufferToCloud = () => {
  const bufferToSend = JSON.stringify({ packetIndex, data: audioBuffer });
  // TODO: TEMP CHANGES!
  // postPacketToCloud({
  //   data: bufferToSend,
  //   activityId,
  //   packetIndex,
  //   type: 'AUDIO',
  // });
  sending = false;
  packetIndex += 1;
  audioBuffer = [];
};

const monitorTypes = {
  NO_ANALYSIS: 0,
  ORIGINAL: 1,
  TWO_SECONDS: 2,
  GENTLE_VOICE: 3,
  LOUD_VOICE: 4,
  VOICE_TRANSITION: 5,
  ONE_SECOND: 6,
  SELF_MANAGED: 7,
};

const parseAudioBuffer = (audioProcessingEvent, enableDSPProcessing) => {
  const { inputBuffer } = audioProcessingEvent;
  const data = inputBuffer.getChannelData(0);
  // convert to base64 String
  const stringArray = convertFloatArrayToBase64(data);
  if (sending) {
    tmpBuffer.push(stringArray);
  } else {
    if (tmpBuffer.length > 0) {
      audioBuffer = tmpBuffer;
      tmpBuffer = [];
    }
    audioBuffer.push(stringArray);
    if (activityId && audioBuffer.length >= QUEUE_SIZE) {
      sending = true;
      sendAudioBufferToCloud();
    }
  }
  if (enableDSPProcessing) {
    addSampleData(data);
  } else {
    sampleSubscribers.forEach((subscriber) => {
      try {
        subscriber(data);
      } catch (error) {
        console.error(error);
      }
    });
  }
};

export const startMicrophoneCalibration = (
  audioConstraints = true,
  stopOnDSPCallback = true,
) => {
  calibrationMeanSquares = [];
  calibratedNoiseFloorMeanSquare = null;
  stopCalibrationOnDSPCallback = stopOnDSPCallback;
  startCalibrateMicrophone(handleDSPInvocations, audioConstraints);
};

const calibrateAudioBuffer = (audioProcessingEvent) => {
  if (CALIBRATE_AUDIO_CONTEXT && CALIBRATE_AUDIO_CONTEXT.state !== 'closed') {
    const { inputBuffer } = audioProcessingEvent;
    const data = inputBuffer.getChannelData(0);
    let sumOfSquares = 0;
    for (let index = 0; index < data.length; index += 1) {
      sumOfSquares += data[index] * data[index];
    }
    calibrationMeanSquares.push(sumOfSquares / data.length);
    calibrate(data);
  }
};

const startCalibrateMicrophone = async (addSampleDataFunction, audioConstraints) => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: audioConstraints,
    });
    /* use the stream */
    handleSampleData = addSampleDataFunction;
    CALIBRATE_AUDIO_CONTEXT = new AudioContext();
    const CALIBRATE_SCRIPT_NODE = CALIBRATE_AUDIO_CONTEXT.createScriptProcessor(BUFFER_SIZE, 1, 1);
    // Give the node a function to process audio events
    CALIBRATE_SCRIPT_NODE.onaudioprocess = calibrateAudioBuffer;
    CALIBRATE_MICROPHONE_STREAM = CALIBRATE_AUDIO_CONTEXT.createMediaStreamSource(stream);
    CALIBRATE_MICROPHONE_STREAM.connect(CALIBRATE_SCRIPT_NODE);
    CALIBRATE_SCRIPT_NODE.connect(CALIBRATE_AUDIO_CONTEXT.destination);
  } catch (err) {
    /* handle the error */
    console.error(err);
  }
};

export const startMicrophone = (
  addSampleDataFunction,
  monitorType,
  enableDSPProcessing = true,
  audioConstraints = true,
) => {
  const requestId = ++microphoneRequestId;
  navigator.mediaDevices
    .getUserMedia({
      video: false,
      audio: audioConstraints,
    })
    .then((stream) => {
      if (requestId !== microphoneRequestId) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      // first set the analysis type
      const monitorCode = monitorTypes[monitorType];
      AUDIO_STREAM = stream;
      const user = getFromLocalStorage(LOCAL_STORAGE_ITEM.USER);
      const lng = user.locale === 'HE' ? 2 : 1;
      if (enableDSPProcessing) {
        setAnalysisType(monitorCode, lng);
      }
      /* use the stream */

      handleSampleData = addSampleDataFunction;
      AUDIO_CONTEXT = new AudioContext();
      SCRIPT_NODE = AUDIO_CONTEXT.createScriptProcessor(BUFFER_SIZE, 1, 1);

      // Give the node a function to process audio events
      SCRIPT_NODE.onaudioprocess = (event) => parseAudioBuffer(event, enableDSPProcessing);

      MICROPHONE_STREAM = AUDIO_CONTEXT.createMediaStreamSource(stream);
      MICROPHONE_STREAM.connect(SCRIPT_NODE);
      SCRIPT_NODE.connect(AUDIO_CONTEXT.destination);
    })
    .catch((err) => {
      /* handle the error */
      console.error(err);
    });
};

const handleDSPInvocations = (type, payload, vect) => {
  switch (type) {
    case DSP_INVOCATION_TYPES.ADD_SAMPLE_DATA:
      // do nothing with this information
      break;
    case DSP_INVOCATION_TYPES.ORIGINAL_DSP_ENGINE_ANALYSIS_GRAPHIC:
      handleSampleData(type, getOriginalGraphicValue());
      break;
    case DSP_INVOCATION_TYPES.END_CALIBRATION:
      if (stopCalibrationOnDSPCallback) stopMicrophoneCalibration();
      break;
    // two seconds per syllable, Loud Voice and Gentle Voice errors
    case ERROR_TYPES.nonGentleOnset:
    case ERROR_TYPES.sharpRiseBeginning:
    case ERROR_TYPES.volumeRiseBeginning:
    case ERROR_TYPES.shapeBeginning:
    case ERROR_TYPES.notGentleBeginning:
    case ERROR_TYPES.excessivePeak:
    case ERROR_TYPES.softPeak:
    case ERROR_TYPES.shapeEnd:
    case ERROR_TYPES.volumeFallEnd:
    case ERROR_TYPES.sharpFallEnd:
    case ERROR_TYPES.nonGentleOffset:
    case ERROR_TYPES.notLoudProduction:
    case ERROR_TYPES.notGentleProduction:
    case ERROR_TYPES.notGentleEnd:
    case ERROR_TYPES.nonGentleOnsetSmm:
    case ERROR_TYPES.noLinkingSmm:
    case ERROR_TYPES.tooFlatSmm:
    case ERROR_TYPES.tooFastSmm:
    case ERROR_TYPES.tooSlowSmm:
      handleSampleData(type, payload);
      break;
    case ERROR_TYPES.nonGentleOnsetOsm:
    case ERROR_TYPES.SoftPeakOsm:
    case ERROR_TYPES.noLinkingOsm:
    case ERROR_TYPES.tooFlatOsm:
    case ERROR_TYPES.tooFastOsm:
    case ERROR_TYPES.tooSlowOsm:
      handleSampleData(type, payload, vect);
      break;
    default:
      break;
  }
};

setDSPCallback(handleDSPInvocations);

export const stopMicrophone = () => {
  microphoneRequestId += 1;
  try {
    if (MICROPHONE_STREAM) {
      MICROPHONE_STREAM.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
    }
    if (AUDIO_CONTEXT && AUDIO_CONTEXT.state !== 'closed') {
      AUDIO_CONTEXT.close();
    }
  } catch (error) {
    console.error(error);
  }
};

export const stopMicrophoneCalibration = () => {
  try {
    if (calibrationMeanSquares.length > 0) {
      const sortedMeanSquares = [...calibrationMeanSquares].sort((first, second) => first - second);
      const noiseFloorIndex = Math.floor((sortedMeanSquares.length - 1) * 0.2);
      calibratedNoiseFloorMeanSquare = sortedMeanSquares[noiseFloorIndex];
    }
    if (CALIBRATE_MICROPHONE_STREAM) {
      CALIBRATE_MICROPHONE_STREAM.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
    }
    if (CALIBRATE_AUDIO_CONTEXT && CALIBRATE_AUDIO_CONTEXT.state !== 'closed') {
      CALIBRATE_AUDIO_CONTEXT.close();
    }
    calibrateCancel();
  } catch (error) {
    console.error(error);
  }
};

export default {
  startMicrophone,
  stopMicrophone,
  setActivityId,
  startMicrophoneCalibration,
  stopMicrophoneCalibration,
  subscribeToMicrophoneSamples,
};
